import type { AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import type { ReactNode } from 'react'
import { allCategoryList } from '@/components/agencies/agencies.mock'
import { AgencyFormHeader } from '@/components/agencies/agency-form-header.molecule'
import { AgencyPreviewChatPanel } from '@/components/agencies/agency-preview-chat/agency-preview-chat-panel.organism'
import { TooltipProvider } from '@/components/ui/tooltip'

import { AgencyFormPanelsView } from './agency-form-panels.view'
import { useAgencyEditorData } from './use-agency-editor-data.hook'
import { useAgencyExportImport } from './use-agency-export-import.hook'
import { useAgencyForm } from './use-agency-form.hook'

interface AgencyCreateEditViewProps {
  agencyResponse?: AgencyResponse | null
  isEditMode?: boolean
}

export const AgencyCreateEditView = ({
  agencyResponse = null,
  isEditMode = false,
}: AgencyCreateEditViewProps): ReactNode => {
  const editorData = useAgencyEditorData()
  const formState = useAgencyForm({ agencyResponse, isEditMode })
  const exportImport = useAgencyExportImport({
    form: formState.form,
    flowDataRef: formState.flowDataRef,
  })

  return (
    <TooltipProvider>
      <div className="agency-create-edit-wrapper flex h-full flex-col overflow-hidden">
        <AgencyFormHeader
          visibility={formState.visibility}
          version={formState.version}
          isLoading={formState.isLoading}
          isSaveDisabled={!formState.form.formState.isDirty && !formState.isFlowDirty}
          onBack={formState.handleBack}
          onSaveDraft={formState.handleSaveDraft}
          onPublishToOrganization={formState.handlePublishToOrganization}
          onSubmitForReview={formState.handleSubmitForReview}
        />
        <div className="agency-create-edit-content flex flex-1 overflow-hidden">
          <AgencyFormPanelsView
            form={formState.form}
            categoryList={allCategoryList}
            logoPreviewUrl={formState.logoPreviewUrl}
            onLogoChange={formState.handleLogoChange}
            onLogoRemove={formState.handleLogoRemove}
            isDisabled={formState.isLoading}
            agencySlug={agencyResponse?.slug}
            {...editorData}
            onFlowChange={formState.handleFlowChange}
            canvasKey={exportImport.canvasKey}
            initialNodeList={formState.flowDataRef.current.nodeList}
            initialEdgeList={formState.flowDataRef.current.edgeList}
            onExport={exportImport.handleExport}
            onImport={exportImport.handleImport}
          />
          <AgencyPreviewChatPanel
            agencyId={agencyResponse?.id ?? 'new-agency'}
            agencySlug={agencyResponse?.slug}
          />
        </div>
      </div>
    </TooltipProvider>
  )
}

AgencyCreateEditView.displayName = 'AgencyCreateEditView'
